import CreateNewPathModal from 'components/form_builder/modal/CreateNewPathModal'
import { copyText } from 'components/utility/copyText'
import axios from 'lib/axios'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

export const CopyItem = ({ onCopy = () => {}, timeToReset = 5000 }) => {
  const [hasCopied, setHasCopied] = useState(false)

  const handleCopy = () => {
    onCopy()
    setHasCopied(true)
    setTimeout(() => {
      setHasCopied(false)
    }, timeToReset)
  }

  return (
    <>
      {hasCopied ? (
        <i className="fa-light fa-circle-check dome-color-go-green"></i>
      ) : (
        <div onClick={handleCopy} className="copy-item dome-d-flex dome-align-center dome-gap3 dome-cursor-pointer">
          <i className="fa-light fa-copy dome-color-aqua copy-item-icon"></i>
          <span className="dome-p2 dome-color-aqua copy-item-text">copy</span>
        </div>
      )}
    </>
  )
}

const PathSearchDataItem = ({ label, value, type, source, onSelect = () => {}, disableCopy = false, onCopy }) => {

  const handleCopyItem = () => {
    copyText(value)
    onCopy(value)
  }

  return (
    <li onClick={onSelect} className="path-search-item">
      <div className="dome-d-flex dome-flex-column">
        <div className="dome-d-flex dome-align-center dome-justify-between">
          <span className="dome-p1 dome-color-darkest-grey">{label}</span>
          {!disableCopy && <CopyItem onCopy={handleCopyItem} />}
        </div>
        <span className="dome-p3 dome-color-med-grey">{value}</span>
        <span className="dome-p3 dome-color-med-grey">{type}</span>
        <span className="dome-p3 dome-color-med-grey">source: {source}</span>
      </div>
    </li>
  )
}

const usePathSearch = ({ formId }) => {
  const [query, setQuery] = useState('')
  const [data, setData] = useState([])
  const [error, setError] = useState(null)
  const [isSearching, setIsSearching] = useState(false)

  const abortControllerRef = useRef(null)

  const debouncedSearch = useCallback(
    debounce(async (query) => {
      try {
        if (abortControllerRef.current) abortControllerRef.current.abort()
        abortControllerRef.current = new AbortController()
        const { data } = await axios.get(`/forms/v3/${formId}/search_path`, {
          params: { term: query },
          signal: abortControllerRef.current.signal,
        })
        setData(data?.list)
      } catch (error) {
        setData([])
        setError(error)
      } finally {
        setIsSearching(false)
      }
    }, 500),
    [formId]
  )

  useEffect(() => {
    if (query === '') return
    setIsSearching(true)
    debouncedSearch(query)
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [query, debouncedSearch])

  const onChange = (e) => setQuery(e.target.value)

  return {
    data,
    error,
    value: query,
    isSearching,
    onChange,
    setData: setQuery,
  }
}

export default function PathSearch({ disableCopy = false, onSelectPathItem = () => {} }) {
  const inputRef = useRef()
  const pathSearchRef = useRef()
  const pathCreationModalRef = useRef()

  const [openPathDropDown, setOpenPathDropDown] = useState(false)
  const [openNewPathModal, setOpenNewPathModal] = useState(false)

  const { formId } = useSelector((state) => state.base)

  const { data, setData, isSearching, value, onChange } = usePathSearch({ formId: formId })

  const showPathSearchDataItems = !isSearching && value !== ''

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        pathSearchRef.current &&
        pathCreationModalRef.current &&
        !pathSearchRef.current.contains(e.target) &&
        !pathCreationModalRef.current.contains(e.target)
      )
        setOpenPathDropDown(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div ref={pathSearchRef} className="path-search-container">
      <div style={{ padding: '12px 18px', background: 'white', border: '1px solid var(--accent, #F0F0F0)' }}>
        <div className="dome-d-flex dome-align-center dome-gap12">
          <i className="fa-regular fa-magnifying-glass"></i>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={onChange}
            placeholder="search paths"
            onFocus={() => setOpenPathDropDown(true)}
            className="path-search-input dome-flex-1"
          />
        </div>
      </div>
      <div className="dome-relative">
        {openPathDropDown && (
          <div className="path-search-dropdown-container">
            <div className="path-search-dropdown-content">
              <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--disabled, #DBDBDB)' }}>
                <button
                  type="button"
                  onClick={() => setOpenNewPathModal(true)}
                  className="dome-btn dome-btn-link dome-btn-link-aqua dome-btn-link-chevron-right"
                >
                  continue with {value}
                </button>
              </div>
              {showPathSearchDataItems && !!data.length && (
                <ul style={{ listStyle: 'none', padding: '0', overflow: 'auto', maxHeight: '200px' }}>
                  {data?.map((item) => {
                    return (
                      <PathSearchDataItem
                        key={item.path}
                        value={item.path}
                        source={item.source}
                        type={item.field?.type}
                        label={item.field?.label}
                        disableCopy={disableCopy}
                        onSelect={() => onSelectPathItem(item)}
                        onCopy={()=> setOpenPathDropDown(false)}
                      />
                    )
                  })}
                </ul>
              )}
              {showPathSearchDataItems && !data.length && (
                <div style={{ padding: '6px 12px 0px 12px' }} className="dome-p3 dome-color-med-grey">
                  No results.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <CreateNewPathModal
        initialPath={value}
        open={openNewPathModal}
        ref={pathCreationModalRef}
        onClose={() => setOpenNewPathModal(false)}
      />
    </div>
  )
}
